import React from "react";
import { productUpdatesRequest, formatPublicationDate } from "../lib/productUpdatesApi.js";

const STATE = { prepared: "Préparé pour le déploiement", scheduled: "Programmé", sent: "Disponible à l’écran", cancelled: "Annulé" };
// Local: one window per deployment; one already committed has left with an earlier deployment.
const LOCAL_STATE = { prepared: "Fenêtre commitée", cancelled: "Fenêtre annulée" };
const toLocalInput = value => value ? new Intl.DateTimeFormat("sv-SE", { timeZone: "America/Toronto", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(value)).replace(" ", "T") : "";

export default function ProductUpdateMessagePanel({ publication, run, local = false, busy = false, committedIds = [] }) {
  const items = (publication.content_json?.sections || []).flatMap(section => section.items);
  const [itemIds, setItemIds] = React.useState(() => items.map(item => String(item.item_id)));
  const [audience, setAudience] = React.useState("all");
  const [userIds, setUserIds] = React.useState([]);
  const [localTime, setLocalTime] = React.useState("");
  const [targets, setTargets] = React.useState(null);
  const [targetError, setTargetError] = React.useState("");
  const [editingId, setEditingId] = React.useState(null);
  const [editingVersion, setEditingVersion] = React.useState(null);
  const [dirty, setDirty] = React.useState(false);
  const formRef = React.useRef(null);
  const messages = (publication.resends_json || []).filter(entry => entry.state);
  const planned = local ? messages.find(entry => entry.state === "prepared" && !committedIds.includes(entry.announcement_id)) : null;
  const committed = local && !planned ? messages.filter(entry => entry.state === "prepared" && committedIds.includes(entry.announcement_id)).at(-1) : null;
  const [reannounce, setReannounce] = React.useState(false);
  const itemKey = items.map(item => item.item_id).join();
  React.useEffect(() => { if (!dirty && !editingId) setItemIds(itemKey ? itemKey.split(",") : []); }, [itemKey]);
  React.useEffect(() => {
    if (audience !== "selected") return;
    let active = true;
    setTargets(null); setTargetError("");
    productUpdatesRequest("/message-targets").then(data => { if (active) setTargets(data); }, failure => { if (active) setTargetError(failure.message); });
    return () => { active = false; };
  }, [audience, publication.version]);
  function change(setter, value) { setter(value); setDirty(true); }
  function toggle(setter, id, checked) { change(setter, current => checked ? [...current, id] : current.filter(value => value !== id)); }
  function reset() { setEditingId(null); setEditingVersion(null); setAudience("all"); setUserIds([]); setItemIds(items.map(item => String(item.item_id))); setLocalTime(""); setDirty(false); setReannounce(false); }
  function edit(entry) {
    setEditingId(entry.announcement_id); setEditingVersion(publication.version); setAudience(entry.audience); setUserIds(entry.user_ids);
    setItemIds(entry.item_ids); setLocalTime(toLocalInput(entry.scheduled_for)); setDirty(false);
    formRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }
  async function prepare(event) {
    event.preventDefault();
    await run(async () => {
      const result = await productUpdatesRequest(`/publications/${publication.id}/messages${editingId ? `/${editingId}` : ""}`, {
        method: editingId ? "PATCH" : "POST", body: { version: editingId ? editingVersion : publication.version, audience,
          user_ids: audience === "selected" ? userIds : [], item_ids: items.map(item => String(item.item_id)).filter(id => itemIds.includes(id)), local_datetime: localTime || null },
      });
      reset();
      return result;
    }, true, local ? "La fenêtre d’annonce est prévue pour le prochain déploiement."
      : localTime ? "Le message est programmé. Le Journal reste inchangé." : "Le message est disponible pour les destinataires choisis. Le Journal reste inchangé.");
  }
  const allowedIds = new Set((targets?.users || []).filter(user => user.eligible).map(user => user.user_id));
  const invalid = !itemIds.length || (audience === "selected" && (!targets || !userIds.length || userIds.some(id => !allowedIds.has(id))));
  function cancel(entry) {
    void run(async () => {
      const result = await productUpdatesRequest(`/publications/${publication.id}/messages/${entry.announcement_id}`, { method: "DELETE", body: { version: publication.version } });
      if (editingId === entry.announcement_id) reset();
      return result;
    }, true, local ? "La fenêtre d’annonce est annulée." : "La préparation du message a été annulée. Un message déjà diffusé reste conservé.");
  }
  const entryDetail = entry => `${entry.audience === "all" ? "Tous les répartiteurs" : `${entry.user_ids.length} utilisateur(s)`} · ${entry.item_ids.length} nouveauté(s)${entry.scheduled_for ? ` · ${formatPublicationDate(entry.scheduled_for)}` : ""}`;
  const form = (
    <form ref={formRef} onSubmit={prepare} className="pu-admin-controls">
      <fieldset disabled={busy} className="pu-admin-controls">
        <label>Destinataires<select aria-label="Destinataires du message" value={audience} onChange={event => change(setAudience, event.target.value)}>
          <option value="all">Tous les répartiteurs</option><option value="selected">Certains utilisateurs</option>
        </select></label>
        {audience === "selected" && <fieldset className="pu-resend-group"><legend>Utilisateurs destinataires</legend>
          {targetError && <p role="alert" className="pu-error">{targetError}</p>}
          {targets === null && !targetError && <small>Chargement des utilisateurs…</small>}
          {targets?.warning && <p className="pu-warning">{targets.warning}</p>}
          {(targets?.users || []).map(user => <label className="pu-item-selection" key={user.user_id}>
            <input type="checkbox" checked={userIds.includes(user.user_id)} disabled={!user.eligible}
              onChange={event => toggle(setUserIds, user.user_id, event.target.checked)} />{user.label}{!user.eligible && " · Indisponible"}
          </label>)}
          {targets && !targets.users.length && <small>Aucun compte éligible disponible.</small>}
        </fieldset>}
        <fieldset className="pu-resend-group"><legend>{local ? "Nouveautés de la fenêtre" : "Nouveautés du message"}</legend>
          {items.map(item => <label className="pu-item-selection" key={item.item_id}><input type="checkbox" checked={itemIds.includes(String(item.item_id))}
            onChange={event => toggle(setItemIds, String(item.item_id), event.target.checked)} />{item.title}</label>)}
        </fieldset>
        <label>{local ? "Ouvrir la fenêtre à partir de (facultatif)" : "Programmer le message (facultatif)"}<input aria-label="Date et heure du message" type="datetime-local" value={localTime}
          onChange={event => change(setLocalTime, event.target.value)} /></label>
        <small>{local ? "Sans horaire, la fenêtre s’ouvre dès le déploiement. Cet horaire ne retarde pas le Journal." : "Sans horaire, le message est disponible immédiatement."}</small>
        <div className="pu-actions"><button type="submit" className="pu-button pu-button--primary" disabled={invalid}>
          {editingId ? (local ? "Enregistrer la fenêtre" : "Enregistrer l’envoi") : local ? "Ajouter la fenêtre d’annonce" : publication.announce_on_publish || messages.some(entry => entry.state === "sent") ? "Réafficher le message" : "Afficher le message"}
        </button>{editingId && <button type="button" className="pu-button" onClick={reset}>Quitter la modification</button>}</div>
      </fieldset>
    </form>
  );
  if (local) {
    const history = messages.filter(entry => entry !== planned && entry !== committed);
    return <section className="pu-schedule-section" aria-label="Fenêtre d’annonce">
      <h4>Fenêtre d’annonce (facultatif)</h4>
      {planned && !editingId ? <div className="pu-resend-group">
        <strong>✓ Fenêtre d’annonce prévue au prochain déploiement</strong>
        <small>{entryDetail(planned)}{planned.scheduled_for ? "" : " · dès le déploiement"}</small>
        <div className="pu-actions">
          <button type="button" className="pu-button" disabled={busy} onClick={() => edit(planned)}>Modifier</button>
          <button type="button" className="pu-button pu-button--danger" disabled={busy} onClick={() => cancel(planned)}>Annuler la fenêtre</button>
        </div>
      </div> : committed && !reannounce ? <div className="pu-resend-group">
        <strong>✓ Fenêtre d’annonce commitée</strong>
        <small>{entryDetail(committed)} · elle s’ouvrira au déploiement de ce commit</small>
        <div className="pu-actions"><button type="button" className="pu-button" disabled={busy} onClick={() => setReannounce(true)}>Réannoncer avec une nouvelle fenêtre</button></div>
      </div> : <>
        {!editingId && <p className="pu-stage-description">La publication est dans le Journal. Ajoutez une fenêtre si vous voulez qu’elle s’ouvre aussi à l’écran des répartiteurs, après le prochain déploiement.</p>}
        {form}
      </>}
      {history.length > 0 && <details><summary>Historique ({history.length})</summary><div className="grid gap-2">
        {history.map(entry => <div className="pu-resend-group" key={entry.announcement_id}>
          <strong>{LOCAL_STATE[entry.state] || STATE[entry.state]}</strong><small>{entryDetail(entry)}</small>
        </div>)}
      </div></details>}
    </section>;
  }
  return <section className="pu-schedule-section" aria-label="Message en ligne">
    <h4>Message en ligne</h4>
    <p className="pu-stage-description">Affichez ou réaffichez un message aux répartiteurs sans modifier le Journal.</p>
    {form}
    {messages.length > 0 && <div className="grid gap-2" aria-label="Messages enregistrés">
      {messages.map(entry => <div className={`pu-resend-group${editingId === entry.announcement_id ? " pu-resend-group--editing" : ""}`} key={entry.announcement_id}>
      <strong>{STATE[entry.state]}{editingId === entry.announcement_id && " · En modification"}</strong>
      <small>{entryDetail(entry)}</small>
      {["prepared", "scheduled"].includes(entry.state) && <div className="pu-actions">
        <button type="button" className="pu-button" disabled={busy || dirty} onClick={() => edit(entry)}>Modifier l’envoi</button>
        <button type="button" className="pu-button pu-button--danger" disabled={busy} onClick={() => cancel(entry)}>Annuler ce message</button>
      </div>}
    </div>)}</div>}
  </section>;
}
